import { AuthenticatedImage } from "@/components/AuthenticatedResource/AuthenticatedImage";
import LabelWithToolTip from "@/components/LabelWithTooltip/LabelWithToolTip";
import {
  useUpdateVideoAssetMutation,
  useVideoMetadataMutation,
} from "@/hooks/assets";
import { useAuth } from "@/hooks/auth";
import { useStore } from "@/services/store";
import Button from "@ingka/button";
import Checkbox from "@ingka/checkbox";
import FormField from "@ingka/form-field";
import InputField from "@ingka/input-field";
import questionMarkCircle from "@ingka/ssr-icon/paths/question-mark-circle";
import Text from "@ingka/text";
import Tooltip from "@ingka/tooltip";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

type Props = {
  assetId: number;
  allOriginalConfirmation: boolean | null;
  fileName: string;
  isLockedForOwner: boolean;
};

const VideoMetadata = ({
  assetId,
  allOriginalConfirmation,
  fileName,
  isLockedForOwner,
}: Props) => {
  const { isIngkaUser } = useAuth().user;
  const updateRelatedAsset = useVideoMetadataMutation(assetId);
  const updateVideoAssetMutation = useUpdateVideoAssetMutation(assetId);
  const { asset, assets, resetValidationError } = useStore(
    useShallow((state) => ({
      asset: state.videoAssets[assetId],
      assets: state.videoAssets,
      resetValidationError: state.resetVideoValidationError,
    })),
  );
  const [inputValue, setInputValue] = useState(asset?.originalName ?? "");

  // Sync local state with store changes
  useEffect(() => {
    setInputValue(asset?.originalName ?? "");
  }, [asset?.originalName]);

  const tooltipText =
    "Select this box if the video is only intended for social media use. If the video can also be used on web and/or app, do not select this box, as transcripts, captions, or subtitles are required by accessibility regulations.";

  return (
    <div className="flex flex-col">
      <Text tagName="span" title={fileName} className="cursor-pointer truncate">
        {fileName}
      </Text>
      <div className="h-[14rem] w-full border-solid">
        <AuthenticatedImage
          className="h-full w-full object-contain pb-0"
          assetId={assetId}
          thumbnail
        />
      </div>
      <div className="mt-space50">
        {!allOriginalConfirmation && (
          <FormField
            shouldValidate={asset?.validationMessage?.some(
              (v) => v.field === "originalName",
            )}
            validation={{
              msg: asset?.validationMessage?.find(
                (v) => v.field === "originalName",
              )?.message,
            }}
          >
            <LabelWithToolTip
              labelText="Name of original video"
              toolTipText="If you are uploading an edited version of an existing video, it is mandatory to fill out the name of the original version."
            />
            <InputField
              disabled={isLockedForOwner}
              id={`asset-${assetId}-videoTitle`}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              name="videoTitle"
              onBlur={() => {
                updateVideoAssetMutation.mutate({
                  [assetId]: {
                    originalName: inputValue.trim(),
                  },
                });
                Object.keys(assets).forEach((key) =>
                  resetValidationError(Number(key), "originalName"),
                );
              }}
            />
          </FormField>
        )}
      </div>
      {isIngkaUser && (
        <div className="mt-space75 flex items-center">
          <Checkbox
            disabled={isLockedForOwner}
            id={`asset-${assetId}-socialMedia`}
            label="For social media only"
            value=""
            onChange={(e) => {
              updateRelatedAsset.mutate({
                isOnlySocialMedia: e.target.checked,
              });
              resetValidationError(assetId, "caption");
              resetValidationError(assetId, "transcript");
            }}
            checked={asset?.isOnlySocialMedia}
            name="socialMedia"
          />
          <Tooltip position="top" tooltipText={tooltipText}>
            <Button
              iconOnly
              size="small"
              ssrIcon={questionMarkCircle}
              type="tertiary"
              aria-label="Information about social media only videos"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default VideoMetadata;
